import { initializeApp } from 'firebase/app';
import {
  browserSessionPersistence,
  getAuth,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  setPersistence,
  signInWithEmailAndPassword,
  User,
  signOut,
  sendPasswordResetEmail,
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import jwtDecode from 'jwt-decode';

import config from '../conf/config';
import { GmdUserInfo } from '../types';

const app = initializeApp(config.firebase);
const db = getFirestore(app);
const storage = getStorage();

const userCredentials: { [key: string]: GmdUserInfo } = {};

export const getDownloadUrl = (imagePath: string) =>
  getDownloadURL(ref(storage, imagePath));

export const isEmailRegistered = async (email: string) => {
  try {
    const q = query(collection(db, 'authorized'), where('email', '==', email));
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return false;
    }
  } catch (error) {
    console.error(error);
  }
  return true;
};

export const getCurrentUser = async () => getAuth().currentUser;

export const isUserSignedIn = () => {
  const user = getAuth().currentUser;
  if (user) {
    return true;
  }
  return false;
};

const updateUserInfo = async (user: User | null) => {
  if (user !== null) {
    const token = await user.getIdToken();
    const decoded: GmdUserInfo = jwtDecode(token);
    userCredentials[user.uid] = decoded;
  }
};

export const signIn = async (
  email: string,
  password: string
): Promise<User> => {
  const auth = getAuth();
  await setPersistence(auth, browserSessionPersistence);
  const userCredential = await signInWithEmailAndPassword(
    auth,
    email,
    password
  );
  getAuth().onIdTokenChanged(async (user: User | null) => {
    updateUserInfo(user);
  });
  updateUserInfo(userCredential.user);
  return userCredential.user;
};

export const createUser = async (
  email: string,
  password: string
): Promise<User> =>
  (await createUserWithEmailAndPassword(getAuth(), email, password)).user;

export const sendVerificationEmail = async (
  user: User,
  url: string
): Promise<void> =>
  await sendEmailVerification(user, {
    url,
    handleCodeInApp: true,
  });

export const sendPasswordReset = async (email: string): Promise<void> =>
  await sendPasswordResetEmail(getAuth(), email);

export const logout = async () => {
  const uid = getAuth().currentUser?.uid || '';
  signOut(getAuth());
  delete userCredentials[uid];
};

export const getCurrentUserToken = async (): Promise<
  string | null | undefined
> =>
  config.appMode === 'dev'
    ? new Promise((resolve) => resolve('mocked'))
    : getAuth().currentUser?.getIdToken();
